import { Select } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';

import { Button } from '../../Button';
import { Text } from '../../Text';
import useStyles from './PaginationComponent.styles';

type PaginationComponentProps = {
  total: number;
  offset: number;
  limit: number;
  unit?: string;
  pageSizes?: string[];
  handlePrevPage: () => void;
  handleNextPage: () => void;
  handleLimitChange: (value: string) => void;
  className?: string;
};

function PaginationComponent({
  total,
  offset = 0,
  limit,
  unit = '',
  pageSizes = ['10', '20', '50'],
  handlePrevPage,
  handleNextPage,
  handleLimitChange,
  className,
}: PaginationComponentProps) {
  const { classes, cx, theme } = useStyles();
  const { t } = useTranslation('common');

return (
  <div className={cx(classes.pagination, className)}>
    <div className={classes.paginationContent}>
      <div className={classes.paginationRange}>
        <Text variant="subhead-x-small">
          {t('table.pagination.range', {
            fromIndex: typeof total === 'number' ? Math.min(offset + 1, total) : '',
            toIndex: typeof total === 'number' ? Math.min(offset + limit, total) : '',
          })}
        </Text>
        <Text variant="body-small" color="gray2.3">
          {t('table.pagination.ofTotalUnits', {
            total: total || '-',
            unit: unit || '',
          })}
        </Text>
      </div>
      <div>
        <Button
          disabled={offset === 0}
          variant="text"
          size="small"
          className={classes.paginationButton}
          onClick={handlePrevPage}
        >
          <IconChevronLeft size={16} />
        </Button>
        <Button
          disabled={offset + limit >= total}
          variant="text"
          size="small"
          className={classes.paginationButton}
          onClick={handleNextPage}
        >
          <IconChevronRight size={16} />
        </Button>
      </div>
    </div>
    {pageSizes.length > 1 && (
      <div className={classes.perPage}>
        <Text variant="body-small" color="gray2.3">
          {t('table.pagination.perPage', {
            unit: unit.charAt(0).toUpperCase() + unit.slice(1),
          })}
        </Text>
        <Select
          data={pageSizes}
          value={limit?.toString()}
          onChange={handleLimitChange}
          w={100}
          styles={{
            input: {
              borderColor: theme.colors.gray2[0],
            },
          }}
        />
      </div>
    )}
  </div>);
}

export default PaginationComponent;
